<template>
  <div id="app">
      <!-- JOYWEB-2794: non-blocking "new version" banner. Always offers a manual refresh;
           the app also auto-reloads itself when it's safe (see version-check logic below). -->
      <div v-if="update_available" class="jw-update-banner">
        <span class="jw-update-text">A new version of BookJoy is available.</span>
        <button class="jw-update-btn" @click="apply_update">Refresh now</button>
      </div>
      <router-view/>
  </div>
</template>

<script>
  export default {
    name : 'app',
    data() {
      return {
        // JOYWEB-2794 — self-update so staff never have to hard-refresh after a deploy.
        update_available: false,
        current_app_asset: null,      // the app.<hash>.js this tab actually loaded
        last_activity_ts: Date.now(), // for idle detection (don't auto-reload an active user)
        version_timer: null,
        first_check_timer: null,
      };
    },
    created() {
      this.current_app_asset = this.get_loaded_app_asset();
    },
    mounted() {
      // Only run in the browser and only if we could identify our own app bundle.
      if (typeof window === 'undefined' || !this.current_app_asset) return;

      ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'].forEach((ev) => {
        window.addEventListener(ev, this.mark_activity, { passive: true });
      });
      // Re-check when the tab regains focus (the overnight case: laptop woken next morning).
      document.addEventListener('visibilitychange', this.on_visibility);
      // And a light poll while the tab is visible.
      this.version_timer = window.setInterval(this.check_for_update, 10 * 60 * 1000); // 10 min
      // First check shortly after load (covers a deploy that happened while this tab was open).
      this.first_check_timer = window.setTimeout(this.check_for_update, 60 * 1000);
    },
    beforeDestroy() {
      if (this.version_timer) window.clearInterval(this.version_timer);
      if (this.first_check_timer) window.clearTimeout(this.first_check_timer);
      document.removeEventListener('visibilitychange', this.on_visibility);
      ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'].forEach((ev) => {
        window.removeEventListener(ev, this.mark_activity);
      });
    },
    methods: {
      mark_activity() { this.last_activity_ts = Date.now(); },

      on_visibility() {
        if (document.visibilityState === 'visible') this.check_for_update();
      },

      // The app bundle this tab is currently running (content-hashed filename).
      get_loaded_app_asset() {
        try {
          var scripts = document.getElementsByTagName('script');
          for (var i = 0; i < scripts.length; i++) {
            var src = scripts[i].getAttribute('src') || '';
            var m = src.match(/js\/app\.[a-z0-9]+\.js/i);
            if (m) return m[0];
          }
        } catch (e) { /* ignore */ }
        return null;
      },

      // Compare our app bundle against the one referenced by the freshly-fetched (no-cache)
      // index.html. If they differ, a new build has been deployed.
      check_for_update() {
        if (this.update_available) { this.maybe_auto_reload(); return; }
        if (document.visibilityState === 'hidden') return;
        var _this = this;
        var base = process.env.BASE_URL || '/';
        try {
          fetch(base + 'index.html?_=' + Date.now(), { cache: 'no-store' })
            .then(function (r) { return r.ok ? r.text() : null; })
            .then(function (html) {
              if (!html) return;
              var m = html.match(/js\/app\.[a-z0-9]+\.js/i);
              var latest = m ? m[0] : null;
              if (latest && _this.current_app_asset && latest !== _this.current_app_asset) {
                _this.update_available = true;
                _this.maybe_auto_reload();
              }
            })
            .catch(function () { /* offline / transient — try again next tick */ });
        } catch (e) { /* ignore */ }
      },

      // Routes that hold unsaved user input — NEVER auto-reload here (hard requirement).
      // The banner still shows so the user can refresh when they choose.
      is_safe_to_reload() {
        var FORM_ROUTES = [
          'Newbooking', 'Editbooking', 'Payment', 'NewCustomer', 'EditCustomer',
          'PreviewBooking', 'Signup', 'signup', 'Login', 'login',
          'forgotpassword', 'resetpassword',
          // JOYWEB-2794 (Codex QA): other routes that hold unsaved input.
          'Product', 'Cart', 'CustAddProducts', 'CustPayment',
        ];
        var name = this.$route && this.$route.name;
        if (name && FORM_ROUTES.indexOf(name) !== -1) return false;
        // Also treat the global loader (a submit/save in flight) as unsafe.
        if (this.$store && this.$store.state && this.$store.state.loader_popup) return false;
        // Generic unsaved-UI guard so we don't have to enumerate every dialog: never reload
        // while a modal/popover/dialog is open (e.g. the Calendar's edit-working-hours /
        // add-break dialogs, which carry unsaved fields) or while a field is focused.
        try {
          if (document.querySelector('.modal, .popover, [role="dialog"], .vm--container, .mx-datepicker-popup')) return false;
          var ae = document.activeElement;
          if (ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return false;
        } catch (e) { return false; }
        // Only reload an idle user (no interaction for 2+ minutes).
        if (Date.now() - this.last_activity_ts < 2 * 60 * 1000) return false;
        return true;
      },

      maybe_auto_reload() {
        if (this.update_available && this.is_safe_to_reload()) this.apply_update();
      },

      apply_update() {
        try { window.location.reload(true); } catch (e) { window.location.reload(); }
      },
    },
  }
</script>

<style>
@import'~bootstrap/dist/css/bootstrap.css';
@import './assets/style.css';
@import './assets/customer_style.css';

/* JOYWEB-2794 update banner — matches the BookJoy design system (Quicksand, pink accent). */
.jw-update-banner {
  position: fixed;
  z-index: 3000;
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 14px;
  background: #ffffff;
  border: 1px solid #EBEFF2;
  border-radius: 40px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  padding: 10px 12px 10px 20px;
  font-family: Quicksand, sans-serif;
  max-width: 92vw;
}
.jw-update-text { font-size: 14px; color: #2b3040; font-weight: 500; }
.jw-update-btn {
  border: 0;
  background: #FB6672;
  color: #ffffff;
  font-family: Quicksand, sans-serif;
  font-weight: 600;
  font-size: 14px;
  border-radius: 40px;
  padding: 8px 18px;
  cursor: pointer;
}
.jw-update-btn:hover { background: #f74e5c; }
</style>
